import { createSelector } from '@reduxjs/toolkit';
import { initialState } from '../calendarMonthSlice.js';
import { eventResourceIdList } from '../../calendar-common/eventResources.js';

/**
 * Root selector. Falls back to `initialState` so the rest of the selectors
 * never have to null-check.
 *
 * @param {Object} state Redux root state
 */
export const selectCalendarMonth = state => state?.calendarMonth || initialState;

/** @returns {string} 'idle' | 'loading' | 'succeeded' | 'failed' */
export const selectMonthStatus = state => selectCalendarMonth(state).status;

/** @returns {string|null} */
export const selectMonthError = state => selectCalendarMonth(state).error;

/** @returns {Object} dateRange */
export const selectDateRange = state => selectCalendarMonth(state).dateRange;

/** @returns {Object} filters */
export const selectFilters = state => selectCalendarMonth(state).filters;

/**
 * One-shot gate flipped by the page once bootstrap has resolved and the real
 * timezone has been seeded into `filters.timezone`. The events effect waits on
 * this so it fetches once with the resolved timezone instead of first firing
 * with the pre-bootstrap `'UTC'` default (a wasted request).
 *
 * @returns {boolean}
 */
export const selectTimezoneResolved = state =>
	selectCalendarMonth(state).timezoneResolved === true;

/**
 * One-shot guard read by the Month View mount effect (sub-epic 10.4).
 * `false` means the auto-select has not yet fired for this session; once the
 * effect seeds `filters.org = [currentOrgUnique]` it dispatches
 * `markOrgAutoSelectApplied` to flip this to `true` and the effect becomes a
 * no-op for the rest of the session.
 *
 * @returns {boolean}
 */
export const selectOrgAutoSelectApplied = state =>
	selectCalendarMonth(state).orgAutoSelectApplied === true;

const selectAllDays = state => selectCalendarMonth(state).days;
import { pickCalendarBootstrap } from '../../calendar-common/viewStoreKeys.js';

const selectAllEvents = state => selectCalendarMonth(state).events;

/**
 * Returns the events that fall on a given day timestamp, in the order
 * stored in the day bucket. Memoized per `(days, events, unixTimestamp)`.
 *
 * @param {number|string} unixTimestamp
 */
export const selectEventsForDay = createSelector(
	[selectAllDays, selectAllEvents, (_state, unixTimestamp) => unixTimestamp],
	(days, events, unixTimestamp) => {
		const bucket = days[String(unixTimestamp)];
		if (!bucket) return [];
		return bucket.eventIds
			.map(id => events[String(id)])
			.filter(ev => ev != null);
	}
);

/** Check if event field matches a scalar or array filter value. */
const matchesFilter = (filterVal, eventVal) => {
	if (filterVal == null) return true;
	if (Array.isArray(filterVal)) {
		if (filterVal.length === 0) return true;
		return filterVal.map(String).includes(String(eventVal));
	}
	if (filterVal === '') return true;
	return String(filterVal) === String(eventVal);
};

const isFilterMatch = (event, filters) => {
	if (!event) return false;
	if (!matchesFilter(filters.org, event.organizationUnique)) return false;
	// `event.locationID` is NOT in `BaseEventsResponseDto` — we don't enrich
	// it synchronously either (race-prone vs `fetchRefs`). When the field is
	// absent we trust the server: if this event came back while a location
	// filter was active, backend's `locations[]` query already matched it.
	// Only enforce client-side when the field IS present (future-proof).
	if (event.locationID != null && !matchesFilter(filters.location, event.locationID)) return false;
	// Dual events occupy several resources (aircraft + instructor) and the
	// DTO's resourceIds order is not stable — match if ANY id matches.
	if (!eventResourceIdList(event).some(rid => matchesFilter(filters.resource, rid))) return false;
	if (!matchesFilter(filters.pilot, event.pilotID)) return false;
	if (!matchesFilter(filters.instructor, event.instructorID)) return false;
	if (filters.searchQuery) {
		const q = String(filters.searchQuery).toLowerCase();
		const haystack = [event.title, event.description, event.pilotName, event.aircraftName]
			.filter(Boolean)
			.join(' ')
			.toLowerCase();
		if (!haystack.includes(q)) return false;
	}
	return true;
};

/**
 * Returns the full list of events filtered by the current filter object.
 * Memoized — recomputes only when `events` or `filters` change.
 */
export const selectFilteredEvents = createSelector(
	[selectAllEvents, selectFilters],
	(events, filters) => Object.values(events).filter(ev => isFilterMatch(ev, filters))
);

/**
 * Resolve the event default length (seconds) for a given org slug.
 *
 * Precedence:
 *   1. user-level override (`userSettings.eventDefaultLength > 0`)
 *   2. org-level default from `orgSettingsMap[matchedOrgId]`
 *   3. dashboard `organizationSettings.eventDefaultLength` (covers the
 *      non-blocking failure of `additional-data` for the current org)
 *   4. 3600 (1h hardcoded fallback so the modal still opens)
 *
 * Note: `eventDefaultLength === 0` from `/user/settings` means "no per-user
 * override" (verified: `UserSettingsManagerTest::testGetSettingsReturnsDefaults`).
 * The `> 0` check correctly falls through to the org-level value.
 *
 * @param {Object} state Redux root state
 * @param {string} [orgUnique] Organization slug (e.g. `formOrgUnique` from
 *   the Create Event modal). When omitted, the selector skips the org map
 *   step and falls through to the dashboard default.
 * @returns {number} seconds (always > 0)
 */
export const selectEventDefaultLengthSec = (state, orgUnique) => {
	// View-aware: reads whichever calendar bootstrap the active page registered
	// (month/week/day) so the org/user default-length setting drives Week and Day
	// create exactly as it does Month — not a silent 3600 fallback.
	const bs = pickCalendarBootstrap(state);

	const userOverride = Number(bs.userSettings?.eventDefaultLength) || 0;
	if (userOverride > 0) return userOverride;

	if (orgUnique) {
		const orgEntry = (bs.userOrgs || []).find(o => o && o.unique === orgUnique);
		if (orgEntry) {
			const v = Number(bs.orgSettingsMap?.[orgEntry.id]?.eventDefaultLength);
			if (Number.isFinite(v) && v > 0) return v;
		}
	}

	const dashboardValue = Number(bs.organizationSettings?.eventDefaultLength);
	if (Number.isFinite(dashboardValue) && dashboardValue > 0) return dashboardValue;

	return 3600;
};
